/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Configuración dinámica de campos para el modal de configuración de entidades
 *
 * Este archivo define qué campos y secciones mostrar para cada tipo de campo.
 * Cada tipo de campo puede tener múltiples secciones (Detalles, Configuración, etc.)
 * y cada sección contiene una lista de fields a renderizar.
 */

// Tipos de campos de formulario disponibles
export type FieldInputType =
  | "text"
  | "number"
  | "textarea"
  | "switch"
  | "select"
  | "multiselect"
  | "date"
  | "time"
  | "file"
  | "radio"
  | "local_list"
  | "file_types"
  | "default_value"
  | "datacenter"
  | "custom";

// Tipo de campo de entidad
export type EntityFieldType =
  | "phone"
  | "identification"
  | "file"
  | "select"
  | "local_list"
  | "interoperability";

// Configuración de un field individual dentro de una sección
export interface FieldConfig {
  // Identificador único del field
  key: string;

  // Tipo de input a renderizar
  type: FieldInputType;

  // Label del campo
  label: string;

  // Placeholder (opcional)
  placeholder?: string;

  // Si el campo es requerido
  required?: boolean;

  // Valor por defecto
  defaultValue?: any;

  // Opciones para select/multiselect
  options?: Array<{ value: string; label: string }>;

  // Validaciones
  validation?: {
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    custom?: (value: any) => string | null; // Retorna mensaje de error o null si es válido
  };

  // Help text o descripción
  helperText?: string;

  // Si el campo está deshabilitado
  disabled?: boolean;

  // Condición para mostrar el campo (basado en otros valores del formulario)
  condition?: (formValues: Record<string, any>) => boolean;

  // Props adicionales específicos del tipo
  customProps?: Record<string, any>;
}

// Configuración de una sección dentro del modal
export interface SectionConfig {
  // Título de la sección
  title: string;

  // Lista de fields en esta sección
  fields: FieldConfig[];

  // Si la sección debe mostrar un separador antes
  showSeparator?: boolean;
}

// Configuración completa para un tipo de campo
export interface FieldTypeConfiguration {
  // Tipo de campo de entidad
  field: EntityFieldType;

  // Secciones a mostrar en el modal
  sections: SectionConfig[];
}

// Array de configuraciones para todos los tipos de campos
export type FieldConfigurations = FieldTypeConfiguration[];

// Importar los datos de configuración
import { fieldConfigurationsData } from "./fieldConfigurationsData";

// Exportar las configuraciones desde el archivo de datos
export const fieldConfigurations: FieldConfigurations = fieldConfigurationsData;

/**
 * Helper para obtener la configuración de un tipo de campo específico
 */
export const getFieldConfiguration = (
  fieldType: EntityFieldType,
): FieldTypeConfiguration | undefined => {
  return fieldConfigurations.find((config) => config.field === fieldType);
};
